import { useAuthStore } from '@/features/auth/auth-store';
import { useAuth } from '@/features/auth/use-auth';
import { ProvisioningOverlay } from '@/features/provisioning/provisioning-overlay';
import { ErrorBoundary } from '@/shared/components/error-boundary';
import { Nav } from '@/shared/components/nav';
import { SpinnerFullScreen } from '@/shared/components/spinner';
import { useResizeObserver } from '@/shared/hooks/use-resize-observer';
import { decodeAccessToken } from '@/utils/jwt';
import { Outlet, createRootRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Toaster } from 'sonner';

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: ErrorBoundary,
});

function RootComponent() {
  useResizeObserver();

  const {
    accessToken,
    setLogoUrl,
    setOrganizationId,
    setServiceTier,
    setServiceTierStatus,
    setTokenLimit,
    setTokenLimitPeriod,
    setUserEmail,
    setUserId,
  } = useAuthStore();

  const { getUserInfo, requestUmaTicket, umaTicketDecoded } =
    useAuth();

  useEffect(() => {
    if (accessToken) {
      void getUserInfo(accessToken).then((userInfo) => {
        if (userInfo) {
          // This is necessary because the backend uses the application
          // whitelist from the access token instead of the one available in
          // the user info endpoint.

          let organizationId: string | undefined;

          try {
            const decodedAccessToken = decodeAccessToken(accessToken);

            organizationId = decodedAccessToken.groups[0];
          } catch (error) {
            console.error(error);
          }

          const customAttributes = userInfo.custom_attributes;
          const serviceTier = customAttributes?.find(
            (attribute) => attribute.key === 'service_tier',
          )?.value;
          const serviceTierStatus = customAttributes?.find(
            (attribute) => attribute.key === 'provisioning_state',
          )?.value;

          setLogoUrl(userInfo.logo_url);
          setOrganizationId(organizationId);
          setServiceTier(serviceTier ?? 'SINGLE');
          setServiceTierStatus(serviceTierStatus ?? 'READY');
          setTokenLimit(userInfo.token_limit ?? 0);
          setTokenLimitPeriod(
            userInfo.token_limit_period ?? 'monthly',
          );
          setUserEmail(userInfo.email);
          setUserId(userInfo.sub);
        }
      });

      void requestUmaTicket();
    }
  }, [
    getUserInfo,
    accessToken,
    setLogoUrl,
    setOrganizationId,
    setServiceTier,
    setServiceTierStatus,
    setTokenLimit,
    setTokenLimitPeriod,
    setUserEmail,
    requestUmaTicket,
    setUserId,
  ]);

  if (!umaTicketDecoded) {
    return <SpinnerFullScreen />;
  }

  return (
    <div className="bg-background text-text flex h-svh w-svw flex-col items-center">
      <div className="mt-2.5 w-full px-3">
        <Nav />
      </div>

      <div className="flex min-h-0 w-full flex-1 overflow-hidden">
        <div className="min-w-0 flex-1 overflow-hidden overflow-y-auto">
          <Outlet />
        </div>
      </div>

      <Toaster />

      <ProvisioningOverlay />

      {/* <TanStackRouterDevtools position="bottom-right" /> */}
    </div>
  );
}
